import React, { useEffect, useRef, useState } from "react";
import Icon from "../../Elements/Icon/Icon.tsx";
import loader from "helpers/loaderUrl.js";
import Button from "../../Elements/Button/Button.js";
import Tooltip from "../../Elements/Tooltip/Tooltip.js";
import SelectElement from "../../Elements/Field/SelectField.js";
import FormValidationMessage from "../../Reusable/Form/FormValidationMessage.js";
import RadioField from "../../Elements/Field/RadioField.js";
import Recaptcha from "../../Reusable/Recaptcha/Recaptcha.tsx";
import RegistrationPasswordField from "./Reusable/RegistrationPasswordField.tsx";
import clsx from "clsx";
import config from "config";
import { awaiter } from "components/Pages/Home/Helpers/helpers.js";
import { RECAPTCHA_APPEAR_DELAY } from "utils/globalConsts.ts";
import { LinkToNewTab } from "../../Elements/Link/Link.js";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import { useForm } from "react-hook-form";
import { CheckBox, DefaultInput, TextArea } from "./defaultFields.js";
import { useRateLimitError } from "hooks/useRateLimitError.ts";
import "./Form.scss";

const checkMark = `${config.api.resource}/assets/images/checkmark.svg`;

export const searchKeyInObject = (incomeArray, data) => {
  if (incomeArray.length > 0) {
    const filteredArray = incomeArray.filter((item) => {
      return item.value === data;
    });
    return filteredArray[0]?.constKey;
  }
};

/**
 * README: HERE IS EXAMPLE OF COMPLETLY CUSTOM ELEMENT WITHIN THE FORMS
 * 1. Create object within fieldsObject of your form, 
 * 1.2. prop { element: "customelement"} - requirement,
 * 1.3. prop { fieldKey: unique string within current form} - requirement,
 * 1.4. prop { render: (props) => <YourCustomComponent {...props} /> } - requirement
 * 1.5. prop { validation:  sturcture the same as another fields} - conditional,
 * 
 * 2. Create your custom react component with porps and use there useCustomController hook with component`s props.
 * 2.1. From this hook you will get object: { fieldControls, hasError };
 * 2.2. fieldControls is an object with properties and methods relevant to your custom element as value/onChange so you can just pass it to your custom input like in example
 * 2.3. hasError is a boolean value with obvious function, it will be true if you have validation and it wasnt pass so you can use it to custom your component in that cases
 * 
 *
 * 
  ExampleField: {
    element: "customelement",
    fieldKey: "ExampleFieldKey",
    validation: {
      required: true,
      invalidMessage: "invalid-email-text",
      reg: /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    },
    render: (props) => <MyComponent {...props} />,
  }
 * 
 * 
  const MyComponent = (props) => {
    const { fieldControls, hasError } = useCustomController(props);

    //start
    //in case you are using your own state within component and want to do custom updates of it
    const [state, setState] = useState();
    //you are changing your state whatever you want
    useEffect(() => {
      fieldControls.onChange(state);
    },[state])
    //finish
    
    return (
      <input
        type="text"
        {...fieldControls}
        className={hasError ? "invalidClassName" : ""}
      />
    );
  };
 */

/**
 * creates an api with initial state
 * @param {string}  fieldsProps - available: (small is default), medium, large, square, line, hidden-label
 * @param {string}  buttonProps - available: small medium large white blank
 * @param {string}  formClass - available: props: wide-form
 * @param {object}  defaultSelectObj - using in case we want to select by default some value
 * @param {object}  externalProps - you can provide some additional props to form and consume it within custom elements
 * in radio or selectlist fields of form,
 * if its radio structure should be as: {RadioFieldName: {value: valuefromyourradiolist}}
 * if its selectlist structure should be as: {SelectListFieldName: {constKey: yourKey, value: yourValue}}
 * @returns a form
 */

const Form = React.forwardRef(
  (
    {
      buttonProps = {},
      fieldsProps = { label: "", field: "" },
      fields = {},
      onSubmit,
      submitProps = {},
      checkAndReturnValuesFunc,
      onCancel,
      cancelProps = {},
      defaultValuesObj = {},
      defaultSelectObj = {},
      withoutRecatpcha = config.env.type === "dev" ? false : false,
      clearAfterSubmit,

      externalProps = {},
      extenralSuccessCancel,
      externalRecaptchaRef,
      // Uses for analytics purposes to track from which form the recaptcha token was sent
      // Action name should be without spaces
      cloudflareActionName,
      className,
      ...restProps
    },
    ref
  ) => {
    /**
     * returning an element to render(text, textarea, checkbox etc.)
     * @param {*} item - object with all fields from fields object
     * @param {*} props - fieldprops such as label and fields classnames
     */

    const { getTextByKey, getBasicSettingsData } = useContent();
    const { isRateLimitError, rateLimitError } = useRateLimitError();
    const globalCaptchaType = getBasicSettingsData()?.recaptchaType;

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [showRecaptchaOnSubmit, setShowRecaptchaOnSubmit] = useState(false);

    /**
     * using react-hook-form object
     */
    const {
      register,
      formState: { errors, isSubmitSuccessful },
      handleSubmit,
      getValues,
      watch,
      setValue,
      setError,
      clearErrors,
      control,
      reset,
      trigger,
    } = useForm();

    watch();

    const returnElement = (item, props) => {
      // object of connection to form data
      const refObject = item.validation
        ? item.validation.reg
          ? {
              required:
                item.validation.required &&
                getTextByKey(item.validation.invalidMessage),
              pattern: {
                value: item.validation.reg,
                message: getTextByKey(item.validation.invalidMessage),
              },
            }
          : item.validation.validate
          ? {
              required: true,
              validate: item.validation.validate,
            }
          : {
              required:
                getTextByKey(item.validation.invalidMessage) ||
                getTextByKey("default-input-invalid-message"),
            }
        : {};

      // input elelement
      switch (item.element) {
        case "customelement":
          return item.render({
            control,
            errors,
            setError,
            item,
            defaultValuesObj,
            handleSubmit,
            clearErrors,
            getValues,
            externalProps,
            setValue,
            setSuccess,
          });

        case "textarea":
          refObject.maxLength = item.validation?.maxLength || 500;
          return <TextArea {...{ errors, item, props, register, refObject }} />;

        case "checkbox":
          return (
            <CheckBox
              {...{ errors, item, props, register, refObject, getValues }}
            />
          );

        case "text":
          refObject.maxLength = item.validation?.maxLength || 100;
          return (
            <label className="text-container">
              {item.linkData ? (
                <LinkToNewTab page={item.linkData.page}>
                  {getTextByKey(getTextByKey(item.label))}
                </LinkToNewTab>
              ) : (
                getTextByKey(item.label)
              )}
            </label>
          );

        case "select":
          return (
            <SelectElement
              {...{
                item,
                register,
                setValue,
                getValues,
                refObject,
                errors,
                clearErrors,
                props,
              }}
              defaultSelect={defaultSelectObj?.[item.fieldKey]}
            />
          );

        case "radio":
          return (
            <RadioField
              {...{ item, register, getValues, setValue, refObject, errors }}
              labelClassName={props.label}
              defaultSelect={defaultSelectObj[item.fieldKey]}
            />
          );

        default:
          refObject.maxLength = item.validation?.maxLength || 100;
          return item.type === "password" ? (
            // in case we dont want to show passwordStrength need to add hidePassStrength prop to password field
            <RegistrationPasswordField
              {...{
                control,
                errors,
                setError,
                item,
                defaultValuesObj,
                handleSubmit,
                clearErrors,
                getValues,
                externalProps,
                labelClassName: props.label,
                setValue,
              }}
            />
          ) : (
            <DefaultInput
              {...{
                errors,
                item,
                props,
                register,
                refObject,
                getValues,
              }}
            />
          );
      }
    };

    // in case i need to cancel success UI from outside
    useEffect(() => {
      if (extenralSuccessCancel) {
        setSuccess(false);
      }
    }, [extenralSuccessCancel]);

    // check if form is valid and return its values and status
    useEffect(() => {
      if (checkAndReturnValuesFunc) {
        checkAndReturnValuesFunc.current = async (noValidate) => {
          if (noValidate) {
            return { values: getValues(), succeed: true };
          } else {
            const res = await trigger(); // boolean true or false
            if (res) {
              return { values: getValues(), succeed: true };
            } else {
              return { values: getValues(), succeed: false };
            }
          }
        };
      }
    }, [checkAndReturnValuesFunc]);

    useEffect(() => {
      if (defaultValuesObj) {
        Object.keys(defaultValuesObj).forEach((defaultObj) => {
          setValue(defaultObj, defaultValuesObj[defaultObj]);
        });
      }
    }, [defaultValuesObj]);

    const recaptchaRef = useRef();
    // logic to pass recaptchaRef to parent component and execute it from there
    useEffect(() => {
      if (externalRecaptchaRef) {
        externalRecaptchaRef.current = recaptchaRef.current;
      }
    }, [externalRecaptchaRef, recaptchaRef.current]);

    const [captchaToken, setCaptchaToken] = useState();
    const [submitData, setSubmitData] = useState();

    useEffect(() => {
      if (captchaToken) {
        onSubmitHandle(submitData);

        setSubmitData(null);
        setCaptchaToken(null);
      }
    }, [captchaToken]);

    const onSubmitHandle = async (data) => {
      //set loader
      setLoading(true);

      let recaptchaResponse = captchaToken;
      if (!withoutRecatpcha && !captchaToken) {
        setShowRecaptchaOnSubmit(true);

        if (globalCaptchaType === "google") {
          await awaiter(RECAPTCHA_APPEAR_DELAY);
          await recaptchaRef.current.executeAsync();
        }

        // Remember submit data and wait when captcha component will return token
        setSubmitData(data);
        return;
      }

      //handle form submit
      const formResult = await onSubmit(
        withoutRecatpcha
          ? data
          : {
              ...data,
              recaptchaResponse: recaptchaResponse,
            }
      );

      setLoading(false);
      // if submit was not successful reset form

      if (formResult?.status === "error") {
        let errorMessage = formResult?.message || formResult?.data;
        const inputName = formResult?.fieldName || "submit";

        if (isRateLimitError({ data: errorMessage }))
          errorMessage = rateLimitError;

        setError(inputName, {
          message: errorMessage,
        });
        setTimeout(() => {
          clearErrors([inputName]);
        }, 3000);
        setSuccess(false);
      } else {
        setSuccess(true);
      }
    };

    const handleChangeFormData = () => {
      setSuccess(false);
    };

    useEffect(() => {
      if (isSubmitSuccessful && clearAfterSubmit) {
        reset();
      }
    }, [isSubmitSuccessful]);

    // clear errors if fields was changed
    useEffect(() => {
      if (fields) {
        clearErrors();
      }
    }, [fields]);

    return (
      <form
        onSubmit={handleSubmit(onSubmitHandle)}
        className={clsx("form-container", className)}
        ref={ref}
        onChange={handleChangeFormData}
        {...restProps}
      >
        {Object.keys(fields).map((index) => {
          const fieldObject = fields[index];
          return (
            <fieldset
              name={index}
              key={index}
              className={`fieldset-container ${
                fieldObject.containerClassName || ""
              }`}
              disabled={loading}
            >
              {fieldObject.tooltip && (
                <div className={"tooltip-container"}>
                  <Tooltip
                    id={fieldObject.title}
                    tooltipTextKey={fieldObject.tooltip.text}
                    top
                  >
                    <div className={`tootltip-icon-container`}>
                      <Icon
                        white
                        medium
                        name={fieldObject.tooltip?.icon || "i"}
                      />
                    </div>
                  </Tooltip>
                </div>
              )}
              {returnElement(fieldObject, fieldsProps)}
            </fieldset>
          );
        })}
        <FormValidationMessage errorMessageObj={errors} />
        {showRecaptchaOnSubmit ? (
          <Recaptcha
            recaptchaRef={recaptchaRef}
            action={
              globalCaptchaType === "cloudflare"
                ? cloudflareActionName
                : undefined
            }
            onSuccess={(token) => setCaptchaToken(token)}
          />
        ) : null}
        {ref || submitProps.hidden ? null : (
          // : loading ? (
          //   <Image className="loader-animation-gif" name="preloader" ext="gif" alt="" title="" />
          // )
          <div className="button-container">
            <Button
              type="submit"
              large={buttonProps?.buttonsSize === "large"}
              small={buttonProps?.buttonsSize === "small"}
              className={submitProps.className}
              title={submitProps.title}
              onMouseDown={submitProps.onMouseDown}
              disabled={loading || success}
              img={{
                src: loading ? loader : success ? checkMark : null,
                title: loading ? "loader" : success ? "checkMark" : null,
              }}
            />
            {onCancel && (
              <>
                <div className="buttons-divider"></div>
                <Button
                  type="button"
                  onClick={onCancel}
                  large={buttonProps?.buttonsSize === "large"}
                  small={buttonProps?.buttonsSize === "small"}
                  className={cancelProps.className}
                  title={cancelProps.title}
                />
              </>
            )}
          </div>
        )}
      </form>
    );
  }
);

export default Form;
